import branchesIcon from '@jetbrains/icons/branches'
import bugExceptionIcon from '@jetbrains/icons/bug-exception'
import bugHourglassIcon from '@jetbrains/icons/bug-hourglass'
import bugOkIcon from '@jetbrains/icons/bug-ok'
import bugSpinnerIcon from '@jetbrains/icons/bug-spinner'
import cancelIcon from '@jetbrains/icons/cancel'
import cancel20pxIcon from '@jetbrains/icons/cancel-20px'
import caretDown10pxIcon from '@jetbrains/icons/caret-down-10px'
import exceptionIcon from '@jetbrains/icons/exception'
import exception20pxIcon from '@jetbrains/icons/exception-20px'
import exceptionEmptyIcon from '@jetbrains/icons/exception-empty'
import exceptionEmpty20pxIcon from '@jetbrains/icons/exception-empty-20px'
import helpIcon from '@jetbrains/icons/help'
import help20pxIcon from '@jetbrains/icons/help-20px'
import hourglassIcon from '@jetbrains/icons/hourglass'
import hourglass20pxIcon from '@jetbrains/icons/hourglass-20px'
import ignoredIcon from '@jetbrains/icons/ignored'
import mutedIcon from '@jetbrains/icons/muted'
import okIcon from '@jetbrains/icons/ok'
import ok20pxIcon from '@jetbrains/icons/ok-20px'
import overtimeIcon from '@jetbrains/icons/overtime'
import pausedIcon from '@jetbrains/icons/paused'
import pinFilledIcon from '@jetbrains/icons/pin-filled'
import spinnerIcon from '@jetbrains/icons/spinner'
import spinner20pxIcon from '@jetbrains/icons/spinner-20px'
import spinnerStaticIcon from '@jetbrains/icons/spinner-static'
import starFilledIcon from '@jetbrains/icons/star-filled'
import tagIcon from '@jetbrains/icons/tag'
import unknownOsIcon from '@jetbrains/icons/unknown-os'
import userCancelIcon from '@jetbrains/icons/user-cancel'
import userCancel20pxIcon from '@jetbrains/icons/user-cancel-20px'
import userExceptionIcon from '@jetbrains/icons/user-exception'
import userException20pxIcon from '@jetbrains/icons/user-exception-20px'
import userExceptionEmptyIcon from '@jetbrains/icons/user-exception-empty'
import userExceptionEmpty20pxIcon from '@jetbrains/icons/user-exception-empty-20px'
import userHourglassIcon from '@jetbrains/icons/user-hourglass'
import userHourglass20pxIcon from '@jetbrains/icons/user-hourglass-20px'
import userOkIcon from '@jetbrains/icons/user-ok'
import userOk20pxIcon from '@jetbrains/icons/user-ok-20px'
import userSpinnerIcon from '@jetbrains/icons/user-spinner'
import userSpinner20pxIcon from '@jetbrains/icons/user-spinner-20px'
import userWarningIcon from '@jetbrains/icons/user-warning'
import userWarning20pxIcon from '@jetbrains/icons/user-warning-20px'
import userWarningEmptyIcon from '@jetbrains/icons/user-warning-empty'
import userWarningEmpty20pxIcon from '@jetbrains/icons/user-warning-empty-20px'
import warningIcon from '@jetbrains/icons/warning'
import warning20pxIcon from '@jetbrains/icons/warning-20px'
import warningEmptyIcon from '@jetbrains/icons/warning-empty'
import warningEmpty20pxIcon from '@jetbrains/icons/warning-empty-20px'
import type {IconType} from '@jetbrains/ring-ui-built/components/icon/icon'
import type {KeyValue} from '@jetbrains/teamcity-ui/dist/utils'
import classnames from 'classnames'

import spinnerArrow20pxIcon from '../../../svg/spinner-arrow-20px.svg?react'
import spinnerArrowIcon from '../../../svg/spinner-arrow.svg?react'
import userSpinnerArrow20pxIcon from '../../../svg/user-spinner-arrow-20px.svg?react'
import userSpinnerArrowIcon from '../../../svg/user-spinner-arrow.svg?react'

import styles from './Icon.module.css'

class Node {
  className: string | null | undefined
  withIcon: boolean
  next: Record<string, Node> | null | undefined

  constructor(className: string | null | undefined, withIcon = false, next?: Record<string, Node>) {
    this.className = className
    this.withIcon = withIcon
    this.next = next
  }
}

const runningColorBranch = {
  red: new Node(styles.red, false),
  green: new Node(styles.green, false),
}
const buildProgressBranch = {
  canceled: new Node(styles.gray, true),
  failedToStart: new Node(styles.red, true, {
    empty: new Node(null, true),
  }),
  queued: new Node(styles.gray, true),
  running: new Node(styles.spinning, true, {
    ...runningColorBranch,
    detached: new Node(null, true, runningColorBranch),
  }),
  finished: new Node(null, false, {
    red: new Node(styles.red, true, {
      empty: new Node(null, true),
    }),
    green: new Node(styles.green, true),
  }),
}
const debugProgressBranch = {
  canceled: new Node(styles.gray, true),
  failedToStart: new Node(styles.red, true),
  queued: new Node(styles.gray, true),
  running: new Node(styles.spinning, true, {
    ...runningColorBranch,
  }),
  finished: new Node(null, false, {
    red: new Node(styles.red, true),
    green: new Node(styles.green, true),
  }),
}
const root = new Node(null, false, {
  ...buildProgressBranch,

  running: new Node(styles.spinning, true, {
    ...buildProgressBranch.running.next,
    static: new Node(classnames(styles.runningStatic, styles.green), true),
  }),
  personal: new Node(classnames(styles.personal, styles.secondary), false, buildProgressBranch),
  my: new Node(classnames(styles.my, styles.secondary), false, buildProgressBranch),
  debug: new Node(classnames(styles.debug, styles.secondary), false, debugProgressBranch),
  ignored: new Node(styles.gray, true),
  paused: new Node(styles.gray, true),
  muted: new Node(styles.gray, true),
  overtime: new Node(styles.red, true),
  warning: new Node(styles.warning, true),
  help: new Node(styles.gray, true),
  helpBlue: new Node(styles.blue, true),
  tag: new Node(styles.gray, true),
  pin: new Node(styles.blue, true),
  star: new Node(styles.yellow, true),
  arrow: new Node(styles.gray, true, {
    down: new Node(null),
    right: new Node(styles.arrowRight),
  }),
  branch: new Node(styles.gray, true),
  dropdownTrigger: new Node(styles.dropdownTrigger),
  dropdownTriggerSpecial: new Node(styles.dropdownTriggerSpecial),
  unknown: new Node(null, false, {
    os: new Node(null, true),
  }),
})
const icons: KeyValue<string, IconType | string> = {
  running: spinnerIcon,
  running_detached: spinnerArrowIcon,
  my_running: userSpinnerIcon,
  debug_running: bugSpinnerIcon,
  my_running_detached: userSpinnerArrowIcon,
  finished_green: okIcon,
  my_finished_green: userOkIcon,
  debug_finished_green: bugOkIcon,
  finished_red: exceptionIcon,
  finished_red_empty: exceptionEmptyIcon,
  my_finished_red: userExceptionIcon,
  debug_finished_red: bugExceptionIcon,
  my_finished_red_empty: userExceptionEmptyIcon,
  canceled: cancelIcon,
  my_canceled: userCancelIcon,
  debug_canceled: bugOkIcon,
  failedToStart: warningIcon,
  failedToStart_empty: warningEmptyIcon,
  my_failedToStart: userWarningIcon,
  my_failedToStart_empty: userWarningEmptyIcon,
  debug_failedToStart: bugExceptionIcon,
  queued: hourglassIcon,
  my_queued: userHourglassIcon,
  debug_queued: bugHourglassIcon,
  unknown_os: unknownOsIcon,
  pin: pinFilledIcon,
  star: starFilledIcon,
  help: helpIcon,
  helpBlue: helpIcon,
  arrow: caretDown10pxIcon,
  branch: branchesIcon,
  running_static: spinnerStaticIcon,
  ignored: ignoredIcon,
  paused: pausedIcon,
  muted: mutedIcon,
  overtime: overtimeIcon,
  warning: warningIcon,
  tag: tagIcon,
}
const bigIcons: KeyValue<string, IconType | string> = {
  running: spinner20pxIcon,
  running_detached: spinnerArrow20pxIcon,
  my_running: userSpinner20pxIcon,
  my_running_detached: userSpinnerArrow20pxIcon,
  finished_green: ok20pxIcon,
  my_finished_green: userOk20pxIcon,
  finished_red: exception20pxIcon,
  finished_red_empty: exceptionEmpty20pxIcon,
  my_finished_red: userException20pxIcon,
  my_finished_red_empty: userExceptionEmpty20pxIcon,
  canceled: cancel20pxIcon,
  my_canceled: userCancel20pxIcon,
  failedToStart: warning20pxIcon,
  failedToStart_empty: warningEmpty20pxIcon,
  my_failedToStart: userWarning20pxIcon,
  my_failedToStart_empty: userWarningEmpty20pxIcon,
  queued: hourglass20pxIcon,
  my_queued: userHourglass20pxIcon,
  help: help20pxIcon,
  helpBlue: help20pxIcon,
}

function getIcon(namePrefix: string, big: boolean): IconType | string | undefined {
  const key = namePrefix.replace('personal', 'my')
  return big ? bigIcons[key] : icons[key]
}

function getAllIconNames(node: Node): string[] {
  if (node.next == null) {
    return []
  }

  const names = []

  for (const modifier in node.next) {
    if (node.next.hasOwnProperty(modifier) && node.next != null) {
      const newNames = getAllIconNames(node.next[modifier]).map(suffix => `${modifier}_${suffix}`)

      if (node.next[modifier].withIcon || newNames.length === 0) {
        names.push(modifier)
      }

      if (newNames.length > 0) {
        names.push(...newNames)
      }
    }
  }

  return names
}

export const allIconNames: string[] = getAllIconNames(root)

export function getIconAndClassNames(
  iconName: string,
  big: boolean,
): [IconType | string | undefined, string] {
  const modifiers = iconName.split('_')
  let node = root
  let iconKey = ''
  let icon
  const classNames = []

  if (big) {
    classNames.push(styles.big)
  }

  for (let i = 0; i < modifiers.length; i++) {
    const currentModifier = modifiers[i]
    const len = i === 0 ? currentModifier.length : iconKey.length + currentModifier.length + 1
    iconKey = iconName.substr(0, len)

    if (node.next?.[currentModifier] != null) {
      node = node.next[currentModifier]
      classNames.push(node.className)

      if (node.withIcon) {
        icon = getIcon(iconKey, big)
      }
    } else {
      break
    }
  }

  return [icon, classnames(classNames)]
}
